import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import axios from 'axios';
import { User, TrendingUp, TrendingDown } from 'lucide-react';


import { BACKEND_URL } from '../../config/api';
const QuadrantView = () => {
  const [quadrantData, setQuadrantData] = useState(null);
  const [selectedQuadrant, setSelectedQuadrant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuadrantData();
  }, []);

  const fetchQuadrantData = async () => {
    try {
      const token = localStorage.getItem('lms_token');
      const response = await axios.get(`${BACKEND_URL}/api/mentor/dashboard/quadrant`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuadrantData(response.data);
    } catch (error) {
      console.error('Error fetching quadrant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quadrantConfig = {
    Q1: {
      title: 'Q1: Champions',
      subtitle: 'High Progress • High Engagement',
      color: 'bg-green-50 border-green-300 hover:border-green-500',
      textColor: 'text-green-700',
      iconColor: 'text-green-600',
      description: 'Students excelling in both progress and engagement'
    },
    Q2: {
      title: 'Q2: Rising Stars',
      subtitle: 'Low Progress • High Engagement',
      color: 'bg-blue-50 border-blue-300 hover:border-blue-500',
      textColor: 'text-blue-700',
      iconColor: 'text-blue-600',
      description: 'Highly engaged students who need guidance to accelerate progress'
    },
    Q3: {
      title: 'Q3: At Risk',
      subtitle: 'Low Progress • Low Engagement',
      color: 'bg-red-50 border-red-300 hover:border-red-500',
      textColor: 'text-red-700',
      iconColor: 'text-red-600',
      description: 'Students requiring immediate attention and intervention'
    },
    Q4: {
      title: 'Q4: Coasting',
      subtitle: 'High Progress • Low Engagement',
      color: 'bg-yellow-50 border-yellow-300 hover:border-yellow-500',
      textColor: 'text-yellow-700',
      iconColor: 'text-yellow-600',
      description: 'Students with good progress but declining engagement'
    }
  };

  if (loading) {
    return (
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Progress Distribution (Quadrants)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="mb-8">
      <Card>
        <CardHeader>
          <CardTitle>Progress Distribution (Quadrants)</CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            X-axis: Course Progress % • Y-axis: Engagement Score
          </p>
        </CardHeader>
        <CardContent>
          {/* Quadrant Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Q2: Top Left */}
            <div
              onClick={() => setSelectedQuadrant(selectedQuadrant === 'Q2' ? null : 'Q2')}
              className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                quadrantConfig.Q2.color
              } ${selectedQuadrant === 'Q2' ? 'ring-2 ring-blue-500' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`font-bold text-lg ${quadrantConfig.Q2.textColor}`}>
                    {quadrantConfig.Q2.title}
                  </h3>
                  <p className="text-sm text-gray-600">{quadrantConfig.Q2.subtitle}</p>
                </div>
                <div className={`flex items-center gap-1 ${quadrantConfig.Q2.iconColor}`}>
                  <TrendingUp className="w-5 h-5" />
                  <span className="font-bold text-xl">
                    {quadrantData?.Q2?.length || 0}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-600 italic">{quadrantConfig.Q2.description}</p>
              
              {selectedQuadrant === 'Q2' && quadrantData?.Q2 && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {quadrantData.Q2.map((student) => (
                    <div key={student.student_id} className="flex items-center gap-3 p-2 bg-white rounded border">
                      {student.profile_photo ? (
                        <img src={student.profile_photo} alt={student.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-700" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium">{student.name}</p>
                        <p className="text-xs text-gray-500">
                          Progress: {student.progress_percentage.toFixed(1)}% • Engagement: {student.engagement_score.toFixed(1)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Q1: Top Right */}
            <div
              onClick={() => setSelectedQuadrant(selectedQuadrant === 'Q1' ? null : 'Q1')}
              className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                quadrantConfig.Q1.color
              } ${selectedQuadrant === 'Q1' ? 'ring-2 ring-green-500' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`font-bold text-lg ${quadrantConfig.Q1.textColor}`}>
                    {quadrantConfig.Q1.title}
                  </h3>
                  <p className="text-sm text-gray-600">{quadrantConfig.Q1.subtitle}</p>
                </div>
                <div className={`flex items-center gap-1 ${quadrantConfig.Q1.iconColor}`}>
                  <TrendingUp className="w-5 h-5" />
                  <span className="font-bold text-xl">
                    {quadrantData?.Q1?.length || 0}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-600 italic">{quadrantConfig.Q1.description}</p>
              
              {selectedQuadrant === 'Q1' && quadrantData?.Q1 && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {quadrantData.Q1.map((student) => (
                    <div key={student.student_id} className="flex items-center gap-3 p-2 bg-white rounded border">
                      {student.profile_photo ? (
                        <img src={student.profile_photo} alt={student.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center">
                          <User className="w-4 h-4 text-green-700" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium">{student.name}</p>
                        <p className="text-xs text-gray-500">
                          Progress: {student.progress_percentage.toFixed(1)}% • Engagement: {student.engagement_score.toFixed(1)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Q3: Bottom Left */}
            <div
              onClick={() => setSelectedQuadrant(selectedQuadrant === 'Q3' ? null : 'Q3')}
              className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                quadrantConfig.Q3.color
              } ${selectedQuadrant === 'Q3' ? 'ring-2 ring-red-500' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`font-bold text-lg ${quadrantConfig.Q3.textColor}`}>
                    {quadrantConfig.Q3.title}
                  </h3>
                  <p className="text-sm text-gray-600">{quadrantConfig.Q3.subtitle}</p>
                </div>
                <div className={`flex items-center gap-1 ${quadrantConfig.Q3.iconColor}`}>
                  <TrendingDown className="w-5 h-5" />
                  <span className="font-bold text-xl">
                    {quadrantData?.Q3?.length || 0}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-600 italic">{quadrantConfig.Q3.description}</p>
              
              {selectedQuadrant === 'Q3' && quadrantData?.Q3 && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {quadrantData.Q3.map((student) => (
                    <div key={student.student_id} className="flex items-center gap-3 p-2 bg-white rounded border">
                      {student.profile_photo ? (
                        <img src={student.profile_photo} alt={student.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center">
                          <User className="w-4 h-4 text-red-700" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium">{student.name}</p>
                        <p className="text-xs text-gray-500">
                          Progress: {student.progress_percentage.toFixed(1)}% • Engagement: {student.engagement_score.toFixed(1)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Q4: Bottom Right */}
            <div
              onClick={() => setSelectedQuadrant(selectedQuadrant === 'Q4' ? null : 'Q4')}
              className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                quadrantConfig.Q4.color
              } ${selectedQuadrant === 'Q4' ? 'ring-2 ring-yellow-500' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className={`font-bold text-lg ${quadrantConfig.Q4.textColor}`}>
                    {quadrantConfig.Q4.title}
                  </h3>
                  <p className="text-sm text-gray-600">{quadrantConfig.Q4.subtitle}</p>
                </div>
                <div className={`flex items-center gap-1 ${quadrantConfig.Q4.iconColor}`}>
                  <TrendingDown className="w-5 h-5" />
                  <span className="font-bold text-xl">
                    {quadrantData?.Q4?.length || 0}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-600 italic">{quadrantConfig.Q4.description}</p>
              
              {selectedQuadrant === 'Q4' && quadrantData?.Q4 && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {quadrantData.Q4.map((student) => (
                    <div key={student.student_id} className="flex items-center gap-3 p-2 bg-white rounded border">
                      {student.profile_photo ? (
                        <img src={student.profile_photo} alt={student.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-yellow-200 flex items-center justify-center">
                          <User className="w-4 h-4 text-yellow-700" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium">{student.name}</p>
                        <p className="text-xs text-gray-500">
                          Progress: {student.progress_percentage.toFixed(1)}% • Engagement: {student.engagement_score.toFixed(1)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuadrantView;
